import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { getReadiness } from '../../api/url_data_agent_evaluations';
import { formatNumber } from '../../helpers/url_data_agent_evaluations_helpers';
import { DEFAULT_READINESS_WINDOW, READINESS_WINDOW_OPTIONS, getReadinessReport } from '../../helpers/url_data_agent_readiness_helpers';
import SegmentedControl from './SegmentedControl';
import { useSearchParamsState } from '../../hooks/useSearchParamsState';

import './SilentEvalReadiness.scss';

const URL_DEFAULTS = { window: DEFAULT_READINESS_WINDOW };

const SilentEvalReadiness = ({ active = true }) => {
  const [urlState, patchUrl] = useSearchParamsState(URL_DEFAULTS);
  const days = READINESS_WINDOW_OPTIONS.some(option => option.value === urlState.window) ? urlState.window : DEFAULT_READINESS_WINDOW;
  const setDays = value => patchUrl({ window: value });
  const [readinessByDays, setReadinessByDays] = useState({});
  const [errorsByDays, setErrorsByDays] = useState({});
  const [fetchingDays, setFetchingDays] = useState(null);
  const readiness = readinessByDays[days];
  const error = errorsByDays[days];

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    setFetchingDays(days);
    getReadiness({ days })
      .then(data => {
        if (cancelled) return;
        setReadinessByDays(cur => ({ ...cur, [days]: data.readiness }));
        setErrorsByDays(cur => ({ ...cur, [days]: null }));
      })
      .catch(err => {
        if (!cancelled) setErrorsByDays(cur => ({ ...cur, [days]: err || 'Could not load the report.' }));
      })
      .finally(() => {
        if (!cancelled) setFetchingDays(null);
      });
    return () => {
      cancelled = true;
    };
  }, [days, active]);

  const report = readiness && getReadinessReport(readiness, days);

  return (
    <div className='silent-eval-readiness'>
      <div className='se-toolbar'>
        <SegmentedControl label='Report period' options={READINESS_WINDOW_OPTIONS} value={days} onChange={setDays} />
        <span className='rd-period-note'>Links started in this period · latest recorded outcomes</span>
        <span className={cn('se-refreshing', { on: fetchingDays != null })} aria-hidden='true' />
      </div>

      {!report ? (
        <div className='se-empty'>{error ? `Could not load the report — ${String(error)}` : 'Loading…'}</div>
      ) : (
        <div className='rd-body'>
          <section>
            <h3>What happened to the links</h3>
            <p className='rd-section-note'>
              Each link appears in one current outcome. Automatic and approved completions include existing matches and new products.
              {report.creationRequestsNote && ` ${report.creationRequestsNote}`}
            </p>
            <div className='rd-funnel'>
              {report.funnel.map(row => (
                <div key={row.key} className={cn('rd-row', `depth-${row.depth}`, row.tone)}>
                  <span className='rd-row-label'>{row.label}</span>
                  <span className='rd-row-count'>{formatNumber(row.count)}</span>
                  <span className='rd-row-side'>
                    {row.share != null && <span className='rd-share'>{Math.round(row.share * 100)}%</span>}
                    {row.outcomes?.map(outcome => (
                      <span key={outcome.key} className={cn('rd-outcome', outcome.tone)}>
                        {outcome.label} <b>{formatNumber(outcome.count)}</b>
                      </span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3>How human reviews compare</h3>
            <p className='rd-section-note'>These rates cover completed reviews of suggestions, not overall APM accuracy. Automatic completions and links awaiting review are excluded.</p>
            <div className='rd-stats'>
              {report.stats.map(stat => (
                <div key={stat.key} className='rd-stat'>
                  <div className='rd-stat-label'>{stat.label}</div>
                  <div className='rd-stat-big'>{stat.big}</div>
                  <div className='rd-stat-sub'>{stat.sub}</div>
                  <div className='rd-stat-note'>{stat.note}</div>
                  <div className='rd-stat-note'>{stat.detail}</div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3>How APM works today</h3>
            <div className='rd-steps'>
              {report.steps.map(step => (
                <div key={step.n} className='rd-step'>
                  <span className='rd-step-n'>{step.n}</span>
                  <div className='rd-step-title'>{step.title}</div>
                  <div className='rd-step-line'>{step.line}</div>
                </div>
              ))}
            </div>
          </section>

          <footer className='rd-footer'>{report.footer} · Costs belong to links started in this period; infrastructure and unrecorded calls are excluded.</footer>
        </div>
      )}
    </div>
  );
};

SilentEvalReadiness.propTypes = {
  active: PropTypes.bool
};

export default SilentEvalReadiness;
